<script setup>
import { computed, ref } from 'vue';
import BaseBubble from 'next/message/bubbles/Base.vue';
import FormattedContent from './FormattedContent.vue';
import AttachmentChips from 'next/message/chips/AttachmentChips.vue';
import TranslationToggle from 'dashboard/components-next/message/TranslationToggle.vue';
import { MESSAGE_TYPES } from '../../constants';
import { useMessageContext } from '../../provider.js';
import { useTranslations } from 'dashboard/composables/useTranslations';

const { content, attachments, contentAttributes, messageType } =
  useMessageContext();

const { hasTranslations, translationContent } =
  useTranslations(contentAttributes);

const renderOriginal = ref(false);

const renderContent = computed(() => {
  if (renderOriginal.value) {
    return content.value;
  }

  if (hasTranslations.value) {
    return translationContent.value;
  }

  return content.value;
});

// MTProto groups bake the author into the content as "Name: text" on EVERY
// message of a run. We strip that prefix from all of them (so it isn't shown
// twice / on follow-ups) using the structured group-sender name. Independent of
// grouping — every message in the run needs stripping, even the non-labelled
// follow-ups.
const prefixName = computed(() => {
  const ca = contentAttributes.value || {};
  return ca.telegramGroupSender?.name || ca.telegram_group_sender?.name || '';
});

const bodyContent = computed(() => {
  const c = renderContent.value || '';
  const name = prefixName.value;
  if (!name) return c;
  if (c === name) return ''; // media-only: content is just the author name
  if (c.startsWith(`${name}: `)) return c.slice(name.length + 2);
  return c;
});

const isTemplate = computed(() => {
  return messageType.value === MESSAGE_TYPES.TEMPLATE;
});

const isEmpty = computed(() => {
  return !content.value && !attachments.value?.length;
});

const handleSeeOriginal = () => {
  renderOriginal.value = !renderOriginal.value;
};
</script>

<template>
  <BaseBubble class="px-4 py-1.5" data-bubble-name="text">
    <div class="gap-3 flex flex-col">
      <span v-if="isEmpty" class="text-n-slate-11">
        {{ $t('CONVERSATION.NO_CONTENT') }}
      </span>
      <!-- Sender label lives in BaseBubble (above the reply-quote, uniform
           across all bubble types). Here we render only the prefix-stripped
           body so the "Name: " baked into group content isn't shown twice. -->
      <FormattedContent v-if="bodyContent" :content="bodyContent" />
      <TranslationToggle
        v-if="hasTranslations"
        class="-mt-3"
        :showing-original="renderOriginal"
        @toggle="handleSeeOriginal"
      />
      <AttachmentChips :attachments="attachments" class="gap-2" />
      <template v-if="isTemplate">
        <div
          v-if="contentAttributes.submittedEmail"
          class="px-2 py-1 rounded-lg bg-n-alpha-3"
        >
          {{ contentAttributes.submittedEmail }}
        </div>
      </template>
    </div>
  </BaseBubble>
</template>

<style>
p:last-child {
  margin-bottom: 0;
}
</style>
